import { useState, useCallback, useEffect, useRef } from "react";
import {
  SIDEBAR_DEFAULT_WIDTH,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_MAX_WIDTH,
  CHAT_AREA_MIN_WIDTH,
  SIDEBAR_RESIZE_HANDLE_WIDTH,
  TASK_QUEUE_MIN_WIDTH,
  TASK_QUEUE_MAX_WIDTH,
  TASK_QUEUE_RESIZE_HANDLE_WIDTH,
} from "./agentSessionsPanelUtils";

/**
 * Encapsulates all sidebar + task-queue resize logic:
 *  - width state, bounds computation, clamping
 *  - mouse-drag handlers
 *  - ResizeObserver / window-resize effect
 */
export default function useResizePanels({
  panelRootRef,
  showSidebar,
  taskQueuePanelWidth,
  setTaskQueuePanelWidth,
}) {
  const isResizingSidebarRef = useRef(false);
  const isResizingTaskQueueRef = useRef(false);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH);

  const getSidebarBounds = useCallback(() => {
    const containerRect = panelRootRef.current?.getBoundingClientRect();
    if (!containerRect) {
      return { min: SIDEBAR_MIN_WIDTH, max: SIDEBAR_MAX_WIDTH };
    }
    const isCompact = containerRect.width < 920;
    const minChatWidth = isCompact
      ? Math.max(120, Math.floor(containerRect.width * 0.45))
      : CHAT_AREA_MIN_WIDTH;
    const minSidebarWidth = isCompact
      ? Math.max(100, Math.floor(containerRect.width * 0.3))
      : SIDEBAR_MIN_WIDTH;
    const sidebarSpaceCap = Math.max(
      0,
      containerRect.width - minChatWidth - SIDEBAR_RESIZE_HANDLE_WIDTH
    );
    const maxSidebarWidth = Math.max(
      minSidebarWidth,
      Math.min(SIDEBAR_MAX_WIDTH, sidebarSpaceCap)
    );
    const effectiveMinSidebarWidth = Math.min(minSidebarWidth, maxSidebarWidth);
    return { min: effectiveMinSidebarWidth, max: maxSidebarWidth };
  }, [panelRootRef]);

  const clampSidebarWidth = useCallback(
    (candidateWidth) => {
      const { min, max } = getSidebarBounds();
      return Math.max(min, Math.min(max, candidateWidth));
    },
    [getSidebarBounds]
  );

  const getTaskQueueBounds = useCallback(() => {
    const containerRect = panelRootRef.current?.getBoundingClientRect();
    if (!containerRect) {
      return { min: TASK_QUEUE_MIN_WIDTH, max: TASK_QUEUE_MAX_WIDTH };
    }
    const isCompact = containerRect.width < 1100;
    const minChatWidth = isCompact
      ? Math.max(220, Math.floor(containerRect.width * 0.45))
      : CHAT_AREA_MIN_WIDTH;
    const minQueueWidth = isCompact
      ? Math.max(220, Math.floor(containerRect.width * 0.32))
      : TASK_QUEUE_MIN_WIDTH;
    const queueSpaceCap = Math.max(
      0,
      containerRect.width -
        minChatWidth -
        SIDEBAR_RESIZE_HANDLE_WIDTH -
        TASK_QUEUE_RESIZE_HANDLE_WIDTH
    );
    const maxQueueWidth = Math.max(
      minQueueWidth,
      Math.min(TASK_QUEUE_MAX_WIDTH, queueSpaceCap)
    );
    const effectiveMinQueueWidth = Math.min(minQueueWidth, maxQueueWidth);
    return { min: effectiveMinQueueWidth, max: maxQueueWidth };
  }, [panelRootRef]);

  const clampTaskQueueWidth = useCallback(
    (candidateWidth) => {
      const { min, max } = getTaskQueueBounds();
      return Math.max(min, Math.min(max, candidateWidth));
    },
    [getTaskQueueBounds]
  );

  const handleSidebarResizeStart = useCallback(
    (event) => {
      if (!showSidebar) return;
      event.preventDefault();
      isResizingSidebarRef.current = true;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [showSidebar]
  );

  const handleTaskQueueResizeStart = useCallback((event) => {
    event.preventDefault();
    isResizingTaskQueueRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  // ResizeObserver — clamp widths when container resizes
  useEffect(() => {
    const node = panelRootRef.current;
    if (!node) return undefined;

    const updateFromContainer = () => {
      setSidebarWidth((prev) => clampSidebarWidth(prev));
      setTaskQueuePanelWidth((prev) => clampTaskQueueWidth(prev));
    };

    updateFromContainer();

    if (typeof ResizeObserver === "function") {
      const resizeObserver = new ResizeObserver(updateFromContainer);
      resizeObserver.observe(node);
      return () => resizeObserver.disconnect();
    }

    window.addEventListener("resize", updateFromContainer);
    return () => window.removeEventListener("resize", updateFromContainer);
  }, [panelRootRef, clampSidebarWidth, clampTaskQueueWidth, setTaskQueuePanelWidth]);

  // Mouse-drag handler
  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!isResizingSidebarRef.current && !isResizingTaskQueueRef.current)
        return;
      const containerRect = panelRootRef.current?.getBoundingClientRect();
      if (!containerRect) return;
      if (isResizingSidebarRef.current) {
        const nextSidebarWidth = event.clientX - containerRect.left;
        setSidebarWidth(clampSidebarWidth(nextSidebarWidth));
      }
      if (isResizingTaskQueueRef.current) {
        const nextQueueWidth = containerRect.right - event.clientX;
        setTaskQueuePanelWidth(clampTaskQueueWidth(nextQueueWidth));
      }
    };

    const stopResize = () => {
      if (!isResizingSidebarRef.current && !isResizingTaskQueueRef.current)
        return;
      isResizingSidebarRef.current = false;
      isResizingTaskQueueRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopResize);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [panelRootRef, clampSidebarWidth, clampTaskQueueWidth, setTaskQueuePanelWidth]);

  // Re-clamp when detail/session changes
  const reclamp = useCallback(() => {
    setSidebarWidth((prev) => clampSidebarWidth(prev));
    setTaskQueuePanelWidth((prev) => clampTaskQueueWidth(prev));
  }, [clampSidebarWidth, clampTaskQueueWidth, setTaskQueuePanelWidth]);

  return {
    sidebarWidth,
    handleSidebarResizeStart,
    handleTaskQueueResizeStart,
    reclamp,
  };
}
